import { Box, Typography, Grid } from "@mui/material";
import { useTranslation } from "hooks/useTranslation";

export default function Guidelines() {
  const { t } = useTranslation();

  return (
    <Grid>
      <Box p={2} mt={0}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {t("guidelines.title", "Annotation Guidelines:")}
        </Typography>

        <Box component="ul" sx={{ pl: 3, listStyleType: "disc" }}>
          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.headsetConnection",
                "Ensure that your headset is connected properly and you can listen to audios clearly"
              )}
            </Typography>
          </li>
          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.copyCanonicalText",
                "Copy the canonical text to the transcribe text box"
              )}
            </Typography>
          </li>
          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.listenCarefully",
                "Listen to each audio clip recording carefully and make any changes necessary to capture what the child has spoken"
              )}
            </Typography>
          </li>
          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.writeExactly",
                "Write exactly what the child has spoken, including hesitations (such as ah, uh, um, eh, etc) and fragmented or partial words"
              )}
            </Typography>
          </li>
          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.partialAudio",
                "For para and stories, in case the child has not read the entire canonical text, for the sentences that have not been spoken, fill the box with a hyphen. Do this only when there is a partial audio, not if the audio is completely blank"
              )}
            </Typography>
          </li>
          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.validateReading",
                "Validate whether the child has read correctly or not"
              )}
            </Typography>
          </li>
          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.backgroundNoise",
                "If there is background noise or any disturbance in the audio, mark it under BG noise"
              )}
            </Typography>

            {/* Examples */}
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-evenly"
              sx={{ pl: 2, mt: 1 }}
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <Typography fontSize={14} fontWeight="medium">
                  {t("guidelines.examples.example", "Example")} 1 :
                </Typography>
                <audio controls src="audios/file_example_WAV_1MG.wav"></audio>
              </Box>

              <Box display="flex" alignItems="center" justifyContent="center">
                <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                  {t("guidelines.examples.example", "Example")} 2 :
                </Typography>
                <audio controls src="audios/Brilient.mp3"></audio>
              </Box>

              <Box display="flex" alignItems="center" justifyContent="center">
                <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                  {t("guidelines.examples.example", "Example")} 3 :
                </Typography>
                <audio controls src="audios/Congrats.mp3"></audio>
              </Box>
            </Box>
          </li>

          <li>
            <Typography fontSize={15} color="#333">
              {t(
                "guidelines.rules.notTranscribable",
                "If the audio is not transcribable, select reason from the following:"
              )}
            </Typography>

            {/* Sublist */}
            <Box component="ul" sx={{ pl: 4, listStyleType: "circle" }}>
              <li>
                <Typography fontSize={15} color="#333">
                  {t(
                    "guidelines.reasons.blank.title",
                    "Blank: Recordings that do not have any speech but which could have noise"
                  )}
                </Typography>
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-evenly"
                  sx={{ pl: 2, mt: 1 }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium">
                      {t("guidelines.examples.example", "Example")} 1 :
                    </Typography>
                    <audio controls src="audios/file_example_WAV_1MG.wav"></audio>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 2 :
                    </Typography>
                    <audio controls src="audios/Brilient.mp3"></audio>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 3 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                </Box>
              </li>

              <li>
                <Typography fontSize={15} color="#333">
                  {t(
                    "guidelines.reasons.irrelevant.title",
                    "Irrelevant: Speaking not related to the test / Mumbling in the background"
                  )}
                </Typography>
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="space-evenly"
                  sx={{ pl: 2, mt: 1 }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium">
                      {t("guidelines.examples.example", "Example")} 1 :
                    </Typography>
                    <audio controls src="audios/file_example_WAV_1MG.wav"></audio>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 2 :
                    </Typography>
                    <audio controls src="audios/Brilient.mp3"></audio>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 3 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                </Box>
              </li>

              <li>
                <Typography fontSize={15} color="#333">
                  {t(
                    "guidelines.reasons.nonTranscribable.title",
                    "Non-transcribable: Gibberish speaking, all difficulty in Reading cases (Stammering / Stuttering / Lisp) resulting in insertions, substituting of many words"
                  )}
                </Typography>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-evenly"
                  sx={{ pl: 2, mt: 1 }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium">
                      {t("guidelines.examples.example", "Example")} 1 :
                    </Typography>
                    <audio controls src="audios/file_example_WAV_1MG.wav"></audio>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 2 :
                    </Typography>
                    <audio controls src="audios/Brilient.mp3"></audio>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 3 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 4 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 5 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 6 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 7 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 8 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography fontSize={14} fontWeight="medium" sx={{ mt: 1 }}>
                      {t("guidelines.examples.example", "Example")} 9 :
                    </Typography>
                    <audio controls src="audios/Congrats.mp3"></audio>
                  </Box>
                </Box>
              </li>
            </Box>
          </li>
        </Box>
      </Box>
    </Grid>
  );
}
